// Shared compact-action icon repertoire. Add matching actions here before copying inline SVG into a mode-specific view.
type RenderZiarcadiaIconOptions = {
    className?: string;
    legacyClassPrefix?: string;
};

type ZiarcadiaIconDefinition = {
    label: string;
    intent: string;
    aliases: readonly string[];
    render: (className: string, options: RenderZiarcadiaIconOptions) => string;
};

const joinClasses = (...classes: Array<string | false | null | undefined>): string =>
    classes.filter(Boolean).join(" ");

const clearPartClass = (part: string, legacyClassPrefix?: string): string =>
    joinClasses(
        `ziarcadia-icon-clear-${part}`,
        legacyClassPrefix ? `${legacyClassPrefix}-${part}` : null
    );

const clearLineClass = (side: "left" | "right", legacyClassPrefix?: string): string =>
    joinClasses(
        "ziarcadia-icon-clear-line",
        `ziarcadia-icon-clear-line-${side}`,
        legacyClassPrefix ? `${legacyClassPrefix}-line` : null,
        legacyClassPrefix ? `${legacyClassPrefix}-line-${side}` : null
    );

export type ZiarcadiaIconName =
    | "calendar"
    | "pause"
    | "musicNote"
    | "previousTrack"
    | "nextTrack"
    | "repeat"
    | "star"
    | "back"
    | "add"
    | "clear"
    | "close"
    | "edit"
    | "copy"
    | "undo"
    | "wordBank"
    | "fileText"
    | "link"
    | "gmail"
    | "googleG"
    | "expand"
    | "magnifier"
    | "translate"
    | "palette"
    | "audio"
    | "eyeOff"
    | "eye"
    | "image"
    | "play"
    | "players"
    | "teams"
    | "download"
    | "upload"
    | "lock"
    | "unlock"
    | "dice";

const ICON_CLASS_SLUGS: Record<ZiarcadiaIconName, string> = {
    calendar: "calendar",
    pause: "pause",
    musicNote: "music-note",
    previousTrack: "previous-track",
    nextTrack: "next-track",
    repeat: "repeat",
    star: "star",
    back: "back",
    add: "add",
    clear: "clear",
    close: "close",
    edit: "edit",
    copy: "copy",
    undo: "undo",
    wordBank: "word-bank",
    fileText: "file-text",
    link: "link",
    gmail: "gmail",
    googleG: "google-g",
    expand: "expand",
    magnifier: "magnifier",
    translate: "translate",
    palette: "palette",
    audio: "audio",
    eyeOff: "eye-off",
    eye: "eye",
    image: "image",
    play: "play",
    players: "players",
    teams: "teams",
    download: "download",
    upload: "upload",
    lock: "lock",
    unlock: "unlock",
    dice: "dice"
};

export const ZIARCADIA_ICON_REPERTOIRE: Record<ZiarcadiaIconName, ZiarcadiaIconDefinition> = {
    pause: {
        label: "Pause", intent: "Pause media playback.", aliases: ["pause video"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14M16 5v14" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>`
    },
    musicNote: {
        label: "Music", intent: "Identify a song or music activity.", aliases: ["song", "note"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 17V6l11-2v11M9 9l11-2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><ellipse cx="6" cy="17.5" rx="3" ry="2.5" fill="currentColor"/><ellipse cx="17" cy="15.5" rx="3" ry="2.5" fill="currentColor"/></svg>`
    },
    previousTrack: {
        label: "Previous track", intent: "Move to the previous song.", aliases: ["previous song"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 5v14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M19 5.5 8.5 12 19 18.5Z" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>`
    },
    nextTrack: {
        label: "Next track", intent: "Move to the next song.", aliases: ["next song"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18 5v14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="m5 5.5 10.5 6.5L5 18.5Z" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>`
    },
    repeat: {
        label: "Replay", intent: "Replay the current song or lyric line.", aliases: ["restart", "replay line"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 9a8 8 0 1 1-.5 5M5 4v5h5" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    },
    star: {
        label: "Rating", intent: "Identify a song rating.", aliases: ["rate", "favorite"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2L3 9.6l6.2-.9Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>`
    },
    audio: {
        label: "Audio",
        intent: "Manage audio recording or diagnostics.",
        aliases: ["waveform", "recording"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M4 10v4m4-7v10m4-13v16m4-13v10m4-7v4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>`
    },
    palette: {
        label: "Palette",
        intent: "Switch the reading surface between paper and dark colors.",
        aliases: ["appearance", "review colors"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M12 3a9 9 0 1 0 0 18h1.2a2.3 2.3 0 0 0 1.7-3.8 1.3 1.3 0 0 1 1-2.2H18a3 3 0 0 0 3-3c0-5-4-9-9-9Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
            <circle cx="7.2" cy="11.8" r="1.25" fill="currentColor"/><circle cx="9.4" cy="7.4" r="1.25" fill="currentColor"/><circle cx="14.4" cy="7.4" r="1.25" fill="currentColor"/><circle cx="17.1" cy="11.1" r="1.25" fill="currentColor"/>
        </svg>`
    },
    calendar: {
        label: "Calendar", intent: "Browse activity history by day.", aliases: ["history", "date"],
        render: className => `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M7 3v4m10-4v4M3 11h18m-13 4h2m4 0h2m-8 3h2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`
    },
    back: {
        label: "Back",
        intent: "Return to the previous or parent surface.",
        aliases: ["arrow left", "return", "go back"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M19 12H5" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" />
                <path d="m11 18-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    add: {
        label: "Add",
        intent: "Create a new item.",
        aliases: ["plus", "new"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" />
            </svg>
        `
    },
    clear: {
        label: "Clear",
        intent: "Remove the current temporary result, drawing, or canvas contents.",
        aliases: ["trash", "wipe", "clear canvas"],
        render: (className, options) => {
            const legacyClassPrefix = options.legacyClassPrefix;

            return `
                <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path class="${clearPartClass(
                        "lid",
                        legacyClassPrefix
                    )}" d="M5 7h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    <path class="${clearPartClass(
                        "handle",
                        legacyClassPrefix
                    )}" d="M9.5 7V5.8A1.8 1.8 0 0 1 11.3 4h1.4a1.8 1.8 0 0 1 1.8 1.8V7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path class="${clearPartClass(
                        "body",
                        legacyClassPrefix
                    )}" d="M7.25 9.2 8 18.75A2.25 2.25 0 0 0 10.25 21h3.5A2.25 2.25 0 0 0 16 18.75l.75-9.55" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path class="${clearLineClass(
                        "left",
                        legacyClassPrefix
                    )}" d="M10.45 11.25v6.15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    <path class="${clearLineClass(
                        "right",
                        legacyClassPrefix
                    )}" d="M13.55 11.25v6.15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
            `;
        }
    },
    close: {
        label: "Close",
        intent: "Dismiss the current overlay or temporary panel.",
        aliases: ["dismiss", "exit panel", "close modal"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M6.25 6.25 17.75 17.75" fill="none" stroke="currentColor" stroke-width="2.15" stroke-linecap="round" />
                <path d="M17.75 6.25 6.25 17.75" fill="none" stroke="currentColor" stroke-width="2.15" stroke-linecap="round" />
            </svg>
        `
    },
    edit: {
        label: "Edit",
        intent: "Correct the current saved item.",
        aliases: ["pencil", "change item"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="m14.5 5.5 4 4M4.5 19.5l1-4 11-11a2.83 2.83 0 0 1 4 4l-11 11-5 1Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
            </svg>
        `
    },
    copy: {
        label: "Duplicate",
        intent: "Make a copy of the current item.",
        aliases: ["duplicate", "clone", "copy item"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="8.25" y="7.25" width="10.5" height="12" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.9" />
                <path d="M15.75 7.1V5.9a1.8 1.8 0 0 0-1.8-1.8H6.9a1.8 1.8 0 0 0-1.8 1.8v9.2a1.8 1.8 0 0 0 1.8 1.8h1.15" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" />
            </svg>
        `
    },
    undo: {
        label: "Undo",
        intent: "Step back one reversible action.",
        aliases: ["undo stroke", "back one"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9 4.75 4.75 9 9 13.25" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5.25 9h9.15a5.35 5.35 0 1 1 0 10.7H11" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    wordBank: {
        label: "Word bank",
        intent: "Open a compact list of available words or case entries.",
        aliases: ["case file", "word list"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M5.5 6.25a2.75 2.75 0 0 1 2.75-2.75h9.25v14.75H8.25A2.75 2.75 0 0 0 5.5 21V6.25Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
                <path d="M8.75 8.5h5.5M8.75 12h5.5M8.75 15.5h3.75" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
        `
    },
    fileText: {
        label: "Text file",
        intent: "Identify a text document or text-based import.",
        aliases: ["document", "text document", "quiz text"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M6.2 3.75h7.1l4.5 4.5v12H6.2a1.7 1.7 0 0 1-1.7-1.7V5.45a1.7 1.7 0 0 1 1.7-1.7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
                <path d="M13.3 3.9v4.5h4.35M8 12h7.5M8 15.5h7.5M8 8.8h2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    link: {
        label: "Link",
        intent: "Copy or share a room link.",
        aliases: ["copy link", "share link", "join link"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9.75 13.95 14.25 9.45" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                <path d="M10.85 7.1 12.2 5.75a4.05 4.05 0 0 1 5.72 5.72l-2 2a4.05 4.05 0 0 1-5.2.44" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M13.15 16.9 11.8 18.25a4.05 4.05 0 0 1-5.72-5.72l2-2a4.05 4.05 0 0 1 5.2-.44" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    gmail: {
        label: "Gmail",
        intent: "Sign in with a Google or Gmail account.",
        aliases: ["google mail", "mail sign in", "gmail sign in"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                <path d="M8.8 11h30.4A3.8 3.8 0 0 1 43 14.8v18.4a3.8 3.8 0 0 1-3.8 3.8H8.8A3.8 3.8 0 0 1 5 33.2V14.8A3.8 3.8 0 0 1 8.8 11Z" fill="#fff" />
                <path d="M8.8 11h5.05L24 19.05 34.15 11h5.05A3.8 3.8 0 0 1 43 14.8v1.72L24 31.5 5 16.52V14.8A3.8 3.8 0 0 1 8.8 11Z" fill="#f1f3f4" />
                <path d="M5 15.2v18A3.8 3.8 0 0 0 8.8 37h5.05V22.2L5 15.2Z" fill="#4285f4" />
                <path d="M34.15 22.2V37h5.05A3.8 3.8 0 0 0 43 33.2v-18l-8.85 7Z" fill="#34a853" />
                <path d="M34.15 11 24 19.05 13.85 11H8.8c-.96 0-1.84.36-2.5.96L24 26l17.7-14.04A3.77 3.77 0 0 0 39.2 11h-5.05Z" fill="#ea4335" />
                <path d="M5 15.2v5.12l8.85 7V22.2L5 15.2Z" fill="#c5221f" />
                <path d="M43 15.2v5.12l-8.85 7V22.2l8.85-7Z" fill="#fbbc04" />
            </svg>
        `
    },
    googleG: {
        label: "Google",
        intent: "Identify Google sign-in.",
        aliases: ["google sign in", "google account", "g logo"],
        render: (className) => `
            <svg class="${className}" viewBox="10 10 20 20" aria-hidden="true" focusable="false">
                <path d="M29.6 20.2273C29.6 19.5182 29.5364 18.8364 29.4182 18.1818H20V22.05H25.3818C25.15 23.3 24.4455 24.3591 23.3864 25.0682V27.5773H26.6182C28.5091 25.8364 29.6 23.2727 29.6 20.2273Z" fill="#4285F4" />
                <path d="M20 30C22.7 30 24.9636 29.1045 26.6181 27.5773L23.3863 25.0682C22.4909 25.6682 21.3454 26.0227 20 26.0227C17.3954 26.0227 15.1909 24.2636 14.4045 21.9H11.0636V24.4909C12.7091 27.7591 16.0909 30 20 30Z" fill="#34A853" />
                <path d="M14.4045 21.9C14.2045 21.3 14.0909 20.6591 14.0909 20C14.0909 19.3409 14.2045 18.7 14.4045 18.1V15.5091H11.0636C10.3864 16.8591 10 18.3864 10 20C10 21.6136 10.3864 23.1409 11.0636 24.4909L14.4045 21.9Z" fill="#FBBC04" />
                <path d="M20 13.9773C21.4681 13.9773 22.7863 14.4818 23.8227 15.4727L26.6909 12.6045C24.9591 10.9909 22.6954 10 20 10C16.0909 10 12.7091 12.2409 11.0636 15.5091L14.4045 18.1C15.1909 15.7364 17.3954 13.9773 20 13.9773Z" fill="#E94235" />
            </svg>
        `
    },
    expand: {
        label: "Expand",
        intent: "Open a compact surface into a larger view.",
        aliases: ["fullscreen", "open larger", "show more"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9 5H5v4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M15 5h4v4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M19 15v4h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5 15v4h4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M9 9 5.75 5.75M15 9l3.25-3.25M15 15l3.25 3.25M9 15l-3.25 3.25" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
        `
    },
    magnifier: {
        label: "Search",
        intent: "Show remaining guesses or a search-style investigation action.",
        aliases: ["tries", "guesses", "inspect", "magnifying glass"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="10.8" cy="10.8" r="5.55" fill="none" stroke="currentColor" stroke-width="2.05" />
                <path d="m15.05 15.05 4.2 4.2" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" />
            </svg>
        `
    },
    translate: {
        label: "Translate",
        intent: "Generate or refresh translations for a block of game terms.",
        aliases: ["languages", "localize", "translation"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="m5 8 6 6M4 14l6-6 2-3M2 5h12M7 2h1" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" />
                <path d="m22 22-5-10-5 10M14 18h6" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    eyeOff: {
        label: "Hide",
        intent: "Temporarily obscure visible setup text.",
        aliases: ["fog", "blur", "private"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M3.75 3.75 20.25 20.25" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" />
                <path d="M9.1 5.25A8.92 8.92 0 0 1 12 4.75c4.8 0 8.15 4.35 9.1 6.1a2.3 2.3 0 0 1 0 2.3 13.2 13.2 0 0 1-2.38 3.02" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14.42 14.42A3.42 3.42 0 0 1 9.58 9.58" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" />
                <path d="M6.64 6.74A13.16 13.16 0 0 0 2.9 10.85a2.3 2.3 0 0 0 0 2.3c.95 1.75 4.3 6.1 9.1 6.1 1.36 0 2.6-.35 3.7-.9" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    eye: {
        label: "Show",
        intent: "Show temporarily hidden content.",
        aliases: ["visible", "reveal", "view"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M2.9 12.05c1.3-2.55 4.55-6.1 9.1-6.1s7.8 3.55 9.1 6.1c-1.3 2.55-4.55 6.1-9.1 6.1s-7.8-3.55-9.1-6.1Z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round" />
                <circle cx="12" cy="12.05" r="2.8" fill="none" stroke="currentColor" stroke-width="1.9" />
            </svg>
        `
    },
    image: {
        label: "Image",
        intent: "Add or identify an image.",
        aliases: ["picture", "photo", "illustration"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="3.75" y="4.75" width="16.5" height="14.5" rx="2" fill="none" stroke="currentColor" stroke-width="1.9" />
                <circle cx="8.2" cy="9.2" r="1.5" fill="none" stroke="currentColor" stroke-width="1.7" />
                <path d="m5.75 17 4.3-4.15 3.05 2.65 2.2-2.05 2.95 3.55" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    play: {
        label: "Play",
        intent: "Play or identify video media.",
        aliases: ["video", "watch", "start"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="m8 5.5 10 6.5-10 6.5v-13Z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round" />
            </svg>
        `
    },
    players: {
        label: "Players",
        intent: "Open or identify a list of room players and results.",
        aliases: ["roster", "standings", "player list", "results list"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="9" cy="8" r="3.1" fill="none" stroke="currentColor" stroke-width="1.9" />
                <path d="M3.9 19.25a5.1 5.1 0 0 1 10.2 0" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" />
                <path d="M15.3 10.1a2.6 2.6 0 1 0-.8-5.08" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
                <path d="M15.35 14.55a4.25 4.25 0 0 1 4.75 4.7" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
            </svg>
        `
    },
    teams: {
        label: "Teams",
        intent: "Identify a collection of classroom teams or orders.",
        aliases: ["groups", "team count", "orders"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.8" />
                <rect x="14" y="3.5" width="6.5" height="6.5" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.8" />
                <rect x="3.5" y="14" width="6.5" height="6.5" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.8" />
                <rect x="14" y="14" width="6.5" height="6.5" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.8" />
            </svg>
        `
    },
    download: {
        label: "Download",
        intent: "Download a local text or document export.",
        aliases: ["save file", "export", "download file"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 4.75v9.1" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" />
                <path d="m8.1 10.3 3.9 3.9 3.9-3.9" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5.5 18.75h13" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" />
            </svg>
        `
    },
    upload: {
        label: "Upload",
        intent: "Upload or restore a local file.",
        aliases: ["restore", "import file", "open file"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 19.25v-9.1" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" />
                <path d="m8.1 13.7 3.9-3.9 3.9 3.9" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5.5 5.25h13" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" />
            </svg>
        `
    },
    lock: {
        label: "Lock",
        intent: "Identify a locked or security-related timer control.",
        aliases: ["secure", "vault", "timer lock"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="5.25" y="10.25" width="13.5" height="10" rx="2.25" fill="none" stroke="currentColor" stroke-width="2" />
                <path d="M8.25 10.25V7.8a3.75 3.75 0 0 1 7.5 0v2.45" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                <path d="M12 14.45v2.35" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
        `
    },
    unlock: {
        label: "Unlock",
        intent: "Open a locked row, value, or security-related control.",
        aliases: ["open lock", "unlock row", "open value"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="5.25" y="10.25" width="13.5" height="10" rx="2.25" fill="none" stroke="currentColor" stroke-width="2" />
                <path d="M8.25 10.25V7.8a3.75 3.75 0 0 1 6.65-2.38" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                <path d="M12 14.45v2.35" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                <path d="M15.9 3.95 18 6.05l-2.1 2.1" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `
    },
    dice: {
        label: "Dice",
        intent: "Roll or randomly choose a player.",
        aliases: ["roll", "random", "chooser"],
        render: (className) => `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="4.5" y="4.5" width="15" height="15" rx="3.1" fill="none" stroke="currentColor" stroke-width="2" />
                <circle cx="8.6" cy="8.6" r="1.2" fill="currentColor" />
                <circle cx="12" cy="12" r="1.2" fill="currentColor" />
                <circle cx="15.4" cy="15.4" r="1.2" fill="currentColor" />
                <circle cx="15.4" cy="8.6" r="1.2" fill="currentColor" />
                <circle cx="8.6" cy="15.4" r="1.2" fill="currentColor" />
            </svg>
        `
    }
};

export const renderZiarcadiaIcon = (
    name: ZiarcadiaIconName,
    options: RenderZiarcadiaIconOptions = {}
): string => {
    const definition = ZIARCADIA_ICON_REPERTOIRE[name];
    const className = joinClasses("ziarcadia-icon", `ziarcadia-icon--${ICON_CLASS_SLUGS[name]}`, options.className);

    return definition.render(className, options);
};
